import {useApp} from '../../../contexts/AppContext';
import {useAuth} from '../../../contexts/UserContext';
import {useEffect, useState} from 'react';
import {
  PageTopVenueName,
} from '../../organisms/CommonParts';
import {
  SectionTitle,
  SubInner,
  TabHeader,
  InputLabel,
  FormInput,
  AddMoreButton,
  RemoveItemButton,
} from '../../atoms/CommonParts';
import styled from 'styled-components';
import config from '../../../config';
import {Link, useNavigate, useLocation} from 'react-router-dom';
import {CountrySelect, AreaSelect} from '../../organisms/CountrySelect';
import {
  useCustomDialog,
  ButtonLayout,
} from '../../../contexts/CustomDialogContext';
import clone from 'rfdc/default';

import BeaconIcon from './BluetoothBeacon-icon.png'

const Page = styled.div`
  @media ${config.layout.wide} {
  }
`;

const FormArea = styled.div`
  border: 1px solid #000000;
  border-radius: 2px;
  margin-top: 30px;
  padding: 20px 20px 30px 20px;
`;

const InputExplanation = styled.div`
  text-align: right;
  margin-top: 10px;
  font-size 12px;
  font-weight: bold;
  line-height: 1;
`;

const RoomsSection = styled.div`
  border-radius: 2px;
  border: 1px solid #000000;
  padding: 20px 16px 30px;
  background-color: #F8F8F8;
  margin-top: 40px;
`;

const RoomItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  button {
    flex: 0 0 auto;
  }
`;

export default function VenueInformationPage() {
  const app = useApp();
  const auth = useAuth();
  const location = useLocation();
  const venue = auth.state.venue;
  const navigate = useNavigate();
  const [country, setCountry] = useState(venue.country);
  const [area, setArea] = useState(venue.area);
  const {customDialog, errorDialogWithAPIError, alertDialog} =
      useCustomDialog();
  const countries = app.state.countries || [];
  const [rooms, setRooms] = useState(clone(auth.state.rooms));
  const devices = auth.state.devices || [];
  const [deletingUsingRooms, setDeletingUsingRooms] = useState(false);

  useEffect(() => {
    app.showHeader();
    document.title = 'Venue Information';
  }, []);

  function handleSubmit(event) {
    event.preventDefault();

    let formData = new FormData(event.currentTarget);
    let zipcode = (formData.get('zipcode') || '').trim().substr(0, 256);
    let address = (formData.get('address') || '').trim().substr(0, 256);
    let phone = (formData.get('phone') || '').trim().substr(0, 256);
    let website = formData.get('website').trim().substr(0, 256);

    let errors = [];
    if (!area) {
      errors.push('- Choose an area.');
    }
    if (!zipcode) {
      errors.push('- Zip code is required.');
    }
    if (!address) {
      errors.push('- Address is required.');
    }
    if (!website) {
      errors.push('- Website is required.');
    }

    let names = formData.getAll('room_name').map((s) => s.trim());
    let newRooms = clone(rooms);
    let nameSet = new Set();
    let dupNames = [];
    newRooms.forEach((r, i) => {
      r.name = names[i];
      if (nameSet.has(names[i].toUpperCase())) {
        dupNames.push(names[i]);
      } else {
        nameSet.add(names[i].toUpperCase());
      }
    });
    if (dupNames.length) {
      errors.push('- The floor/room name already exists.');
    } else if (newRooms.some((r) => !r.name)) {
      errors.push('- Room/floor is required');
    }

    if (errors.length) {
      (async function() {
        await customDialog({
          buttons: ButtonLayout.None,
          description_element: (
              <ul>
                {errors.map((e, i) => {
                  return <li key={i}>{e}</li>;
                })}
              </ul>
          ),
        });
      })();
      return;
    }

    let newVenue = {
      country,
      area,
      name: venue.name,
      zipcode,
      address,
      phone,
      website,
    };

    newRooms.forEach((r) => {
      if (r.room_id < 0) {
        delete r.room_id;
      }
    });

    (async () => {
      if (deletingUsingRooms) {
        let b = await customDialog({
          buttons: [
            {
              label: 'Yes, delete',
              positive: true,
            },
            {
              label: 'No, cancel',
              positive: false,
            },
          ],
          description_element: (
              <span>
              You are going to delete the room/floor connected to your device.
              Are you sure to continue?
            </span>
          ),
        });
        if (b != 0) {
          return;
        }
      }

      try {
        await auth.doUpdateVenueAndRooms(newVenue, newRooms);
        await alertDialog({
          description_element: (
              <span>Changes have been saved successfully.</span>
          ),
        });
        window.location.reload();
      } catch (e) {
        console.error(e);
        await errorDialogWithAPIError(e);
      }
    })();
  }

  function onSelectCountry(_country) {
    if (_country) {
      setCountry(_country);
      let c = countries.find((_c) => _c.country == _country);
      if (c && c.areas.length == 1) {
        setArea(c.areas[0].area);
      } else {
        setArea('');
      }
    }
  }

  function onSelectArea(_area) {
    if (_area) {
      setArea(_area);
    }
  }

  const onClickAddMore = (event) => {
    event.preventDefault();
    let newRooms = clone(rooms);
    newRooms.push({
      room_id: Math.floor(1000000 * Math.random()) * -1,
      name: '',
    });
    setRooms(newRooms);
  };

  const removeRoom = (index) => {
    let room_id = rooms[index].room_id;
    if (
        -1 < room_id &&
        devices.length &&
        devices.some((d) => d.room_id == room_id)
    ) {
      setDeletingUsingRooms(true);
    }
    let newRooms = clone(rooms);
    newRooms.splice(index, 1);
    setRooms(newRooms);
  };

  return (
      <Page>
        <PageTopVenueName/>
        <SectionTitle>Setup</SectionTitle>
        <TabHeader
            buttons={[
              {
                title: 'Venue',
              },
              {
                title: 'Device',
              },
            ]}
            selectedIndex={0}
            onClickTab={() => {
              navigate(`/devices/${location.search || ''}`);
            }}
            style={{
              marginTop: 20,
            }}
        />
        <FormArea>
          <form onSubmit={handleSubmit}>
            <InputLabel required={false}
                        style={{
                          'marginTop': 0,
                        }}
            >Country</InputLabel>
            <CountrySelect country={country} onSelectCountry={onSelectCountry}/>
            <InputLabel required={false}>Area</InputLabel>
            <AreaSelect
                country={country}
                area={area}
                onSelectArea={onSelectArea}
            />
            <InputLabel required={false}>Zip code</InputLabel>
            <FormInput
                type="text"
                required
                name="zipcode"
                defaultValue={venue.zipcode}
            />
            <InputLabel required={false}>Address</InputLabel>
            <FormInput
                type="text"
                required
                name="address"
                defaultValue={venue.address}
            />
            <InputExplanation>Room/House No., Street Name</InputExplanation>
            <InputLabel required={false}>Phone number</InputLabel>
            <FormInput type="text" name="phone" defaultValue={venue.phone}/>
            <InputExplanation>with country code</InputExplanation>
            <InputLabel required={false}>Website</InputLabel>
            <FormInput
                type="url"
                required
                name="website"
                defaultValue={venue.website}
                placeholder={'https://'}
            />
            <RoomsSection>
              <InputLabel required={false} style={{
                'marginTop': 0,
              }}>Room/floor</InputLabel>
              {rooms.map((room, i) => {
                return (
                    <RoomItem key={room.room_id}>
                      <input
                          type="hidden"
                          value={room.room_id}
                          name="room_id"
                      ></input>
                      <FormInput
                          type="text"
                          required
                          name="room_name"
                          defaultValue={room.name || ''}
                      />

                      {
                        room.beacon_id ?
                            (
                                <img src={BeaconIcon} alt="Bluetooth Beacon" style={{width: 20, height: 20, cursor: 'pointer'}}

                                onClick={async ()=>{
                                  await alertDialog({
                                    description_element: (
                                        <div>
                                          <p>Bluetooth Beacon Setting Value</p>
                                          <p>Major: {room.beacon_id_major.toUpperCase()} (HEX)</p>
                                          <p>Minor: {room.beacon_id_minor.toUpperCase()} (HEX)</p>
                                        </div>
                                    ),
                                  });
                                }}
                                />
                            ) : <></>
                      }

                      <RemoveItemButton
                          onClick={(event) => {
                            event.preventDefault();
                            removeRoom(i);
                          }}
                          disabled={rooms.length == 1 && i == 0}
                      />
                    </RoomItem>
                );
              })}

              <AddMoreButton
                  onClick={onClickAddMore}
                  style={{
                    margin: '30px auto 0',
                    display: 'block',
                  }}
              >
                Add another room/floor
              </AddMoreButton>
            </RoomsSection>
            <button
                type="submit"
                className="dark-button center-fixed primary"
                style={{
                  margin: '30px auto 0px',
                }}
            >
              <span>Save changes</span>
            </button>
          </form>
        </FormArea>
        <div
            style={{
              textAlign: 'center',
              marginTop: 30
            }}
        >
          <Link
              to="/contact/"
              state={{
                category: 'Request to delete my venue account',
              }}
              style={{
                fontSize: 14,
                fontWeight: 'bold',
                color: '#000000',
              }}
          >
            Delete the venue information
          </Link>
        </div>
      </Page>
  );
}
